import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input, Select, Toggle } from '@bbpos/ui';
import { productService } from '@/services/product.service';
import { buildProductSearchParams } from './purchase-order-products';

/** A different item the supplier sent for one PO line (POS comments item 10). */
export interface SubstituteDraft {
  product_id: string;
  name: string;
  quantity: string;
  unit_cost: string;
  close_original: boolean;
}

export const emptySubstitute = (): SubstituteDraft => ({ product_id: '', name: '', quantity: '', unit_cost: '', close_original: true });

/** Why a substitute cannot be received as typed, or null. */
export function substituteError(s: SubstituteDraft): string | null {
  if (!s.product_id) return 'Pick the item that arrived';
  if (!(parseFloat(s.quantity) > 0)) return 'Enter the quantity that arrived';
  if (s.unit_cost.trim() && !(parseFloat(s.unit_cost) > 0)) return 'Enter a valid cost';
  return null;
}

/**
 * "Received something else instead?" on a Receive Goods line. The item is added
 * to the PO in place of this line and received in the same submission.
 */
export function SubstituteEditor({
  originalName, value, onChange, symbol,
}: { originalName: string; value: SubstituteDraft; onChange: (next: SubstituteDraft) => void; symbol: string }) {
  const [search, setSearch] = useState('');
  const { data } = useQuery({
    queryKey: ['products', 'po-substitute', search],
    queryFn: () => productService.list(buildProductSearchParams(search)),
  });
  const options = [
    ...(value.product_id && !(data?.data ?? []).some((p) => p.id === value.product_id) ? [{ value: value.product_id, label: value.name }] : []),
    ...(data?.data ?? []).map((p) => ({ value: p.id, label: `${p.name} (${p.sku})` })),
  ];
  const err = substituteError(value);

  return (
    <div className="col-span-full rounded-lg border border-violet-200 bg-gradient-to-r from-violet-50 to-indigo-50 p-3 animate-fade-in dark:border-violet-800 dark:from-violet-900/20 dark:to-indigo-900/20">
      <p className="mb-2 text-xs font-semibold text-violet-800 dark:text-violet-200">Item received instead of {originalName}</p>
      <div className="grid gap-2 sm:grid-cols-4">
        <div className="sm:col-span-2 space-y-1">
          <Input value={search} onChange={setSearch} placeholder="Search products…" aria-label="Search the substitute" />
          <Select
            variant="searchable"
            options={options}
            value={value.product_id}
            onChange={(v) => {
              const id = typeof v === 'string' ? v : '';
              onChange({ ...value, product_id: id, name: (data?.data ?? []).find((p) => p.id === id)?.name ?? value.name });
            }}
            placeholder="Select the item that arrived"
          />
        </div>
        <Input type="number" value={value.quantity} onChange={(v) => onChange({ ...value, quantity: v })} placeholder="Quantity" aria-label="Substitute quantity" />
        <Input type="number" value={value.unit_cost} onChange={(v) => onChange({ ...value, unit_cost: v })} placeholder={`Unit cost (${symbol})`} aria-label="Substitute unit cost" />
      </div>
      <div className="mt-2">
        <Toggle
          checked={value.close_original}
          onChange={(next) => onChange({ ...value, close_original: next })}
          label={`Don't expect the rest of ${originalName}`}
          size="sm"
        />
      </div>
      <p className="mt-1 text-[11px] text-violet-700 dark:text-violet-300">
        Leave the cost blank to use {originalName}&apos;s cost after discount. The item is added to the purchase order as a new line.
      </p>
      {err && <p className="mt-1 text-[11px] text-danger-600">{err}</p>}
    </div>
  );
}
